/**
 * CardRanker — Drag-and-drop ranking of cards for expert feedback.
 *
 * Displays the ML top-N cards in order, lets the expert reorder them,
 * move cards to "non-pertinent", and add cards from the legal moves.
 *
 * Exposes an imperative handle `addOrToggleCard(card)` so the parent
 * can forward clicks from the hand (click-to-rank: successive clicks
 * build the ranking in order of preference).
 */

import { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Card from './Card';
import type { CardNumber } from '../types/game';

// ============================================
// Types
// ============================================

export interface RankedCard {
  card: number;
  card_name: string;
  ml_score: number;
  ml_rank: number;
}

export interface CardRankingResult {
  card_id: number;
  expert_rank: number;
  ml_rank: number;
  ml_score: number;
}

export interface CardRankerHandle {
  addOrToggleCard: (card: CardNumber) => void;
}

interface CardRankerProps {
  mlTop: Array<{ card: number; card_name: string; confidence: number }>;
  legalMoves: CardNumber[];
  onChange: (rankings: CardRankingResult[]) => void;
}

// ============================================
// Sortable card item
// ============================================

function SortableCardItem({
  id,
  card,
  rank,
  mlRank,
  mlScore,
  onRemove,
}: {
  id: string;
  card: number;
  rank: number;
  mlRank: number;
  mlScore: number;
  onRemove: (card: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="card-rank-item" {...attributes} {...listeners}>
      <span className="card-rank-badge">{rank}</span>
      <div className="card-rank-card">
        <Card card={card as CardNumber} size="small" />
      </div>
      <div className="card-rank-info">
        <span className="card-rank-ml-score">{Math.round(mlScore * 100)}%</span>
        {mlRank > 0 && <span className="card-rank-ml-rank">ML: #{mlRank}</span>}
      </div>
      <button
        className="card-rank-remove"
        onClick={(e) => { e.stopPropagation(); onRemove(card); }}
        title="Sortir (non pertinent)"
      >
        ✕
      </button>
    </div>
  );
}

// ============================================
// CardRanker component
// ============================================

const CardRanker = forwardRef<CardRankerHandle, CardRankerProps>(function CardRanker(
  { mlTop, legalMoves, onChange },
  ref,
) {
  const mlTopKey = mlTop.map(c => c.card).join(',');
  const prevMlTopKey = useRef(mlTopKey);

  const buildInitialCards = useCallback(() =>
    mlTop.map((c, i) => ({
      card: c.card,
      card_name: c.card_name,
      ml_score: c.confidence,
      ml_rank: i + 1,
    })),
    [mlTop],
  );

  const [pertinent, setPertinent] = useState<RankedCard[]>(buildInitialCards);
  const [excluded, setExcluded] = useState<RankedCard[]>([]);
  const [showAddPicker, setShowAddPicker] = useState(false);

  // Reset when ML suggestions change
  useEffect(() => {
    if (prevMlTopKey.current !== mlTopKey) {
      prevMlTopKey.current = mlTopKey;
      const cards = buildInitialCards();
      setPertinent(cards);
      setExcluded([]);
      setShowAddPicker(false);
      onChange(cards.map((c, i) => ({
        card_id: c.card,
        expert_rank: i + 1,
        ml_rank: c.ml_rank,
        ml_score: c.ml_score,
      })));
    }
  }, [mlTopKey, buildInitialCards, onChange]);

  // Emit initial rankings on mount
  useEffect(() => {
    const cards = buildInitialCards();
    onChange(cards.map((c, i) => ({
      card_id: c.card,
      expert_rank: i + 1,
      ml_rank: c.ml_rank,
      ml_score: c.ml_score,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const emitChange = useCallback((cards: RankedCard[]) => {
    onChange(cards.map((c, i) => ({
      card_id: c.card,
      expert_rank: i + 1,
      ml_rank: c.ml_rank,
      ml_score: c.ml_score,
    })));
  }, [onChange]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setPertinent(prev => {
      const oldIndex = prev.findIndex(c => `card-${c.card}` === active.id);
      const newIndex = prev.findIndex(c => `card-${c.card}` === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  const handleRemove = useCallback((card: number) => {
    setPertinent(prev => {
      const item = prev.find(c => c.card === card);
      if (!item) return prev;
      const next = prev.filter(c => c.card !== card);
      setExcluded(ex => {
        if (ex.some(c => c.card === card)) return ex;
        return [...ex, item];
      });
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  const handleRestore = useCallback((card: number) => {
    setExcluded(prev => {
      const item = prev.find(c => c.card === card);
      if (!item) return prev;
      const next = prev.filter(c => c.card !== card);
      setPertinent(p => {
        const updated = [...p, item];
        emitChange(updated);
        return updated;
      });
      return next;
    });
  }, [emitChange]);

  const findMlInfo = useCallback((card: number) => {
    const idx = mlTop.findIndex(c => c.card === card);
    if (idx >= 0) return { ml_score: mlTop[idx].confidence, ml_rank: idx + 1, card_name: mlTop[idx].card_name };
    return { ml_score: 0, ml_rank: 0, card_name: `Carte ${card}` };
  }, [mlTop]);

  // Click-to-rank: called when user clicks a card in their hand
  const addOrToggleCard = useCallback((card: CardNumber) => {
    const cardNum = card as number;

    // If card is in pertinent list → remove it (toggle off)
    const inPertinent = pertinent.find(c => c.card === cardNum);
    if (inPertinent) {
      handleRemove(cardNum);
      return;
    }

    // If card is in excluded list → restore it
    const inExcluded = excluded.find(c => c.card === cardNum);
    if (inExcluded) {
      handleRestore(cardNum);
      return;
    }

    // Otherwise add it at the end of the pertinent list
    const info = findMlInfo(cardNum);
    const newItem: RankedCard = {
      card: cardNum,
      card_name: info.card_name,
      ml_score: info.ml_score,
      ml_rank: info.ml_rank,
    };
    setPertinent(prev => {
      const next = [...prev, newItem];
      emitChange(next);
      return next;
    });
  }, [pertinent, excluded, findMlInfo, handleRemove, handleRestore, emitChange]);

  useImperativeHandle(ref, () => ({ addOrToggleCard }), [addOrToggleCard]);

  const addableCards = useMemo(() => {
    const usedSet = new Set([...pertinent.map(c => c.card), ...excluded.map(c => c.card)]);
    return legalMoves.filter(c => !usedSet.has(c));
  }, [pertinent, excluded, legalMoves]);

  const handleAddCard = useCallback((card: CardNumber) => {
    const info = findMlInfo(card as number);
    const newItem: RankedCard = {
      card: card as number,
      card_name: info.card_name,
      ml_score: info.ml_score,
      ml_rank: info.ml_rank,
    };
    setPertinent(prev => {
      const next = [...prev, newItem];
      emitChange(next);
      return next;
    });
    setShowAddPicker(false);
  }, [emitChange, findMlInfo]);

  return (
    <div className="card-ranker">
      <div className="card-ranker-section">
        <h5 className="card-ranker-title">
          Pertinentes
          <span className="card-ranker-hint">Cliquez sur les cartes en main pour classer</span>
        </h5>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={pertinent.map(c => `card-${c.card}`)}
            strategy={verticalListSortingStrategy}
          >
            {pertinent.map((c, i) => (
              <SortableCardItem
                key={c.card}
                id={`card-${c.card}`}
                card={c.card}
                rank={i + 1}
                mlRank={c.ml_rank}
                mlScore={c.ml_score}
                onRemove={handleRemove}
              />
            ))}
          </SortableContext>
        </DndContext>

        {pertinent.length === 0 && (
          <div className="card-ranker-empty">
            Cliquez sur les cartes de la main pour les classer par ordre de pertinence
          </div>
        )}

        {addableCards.length > 0 && (
          <div className="card-ranker-add">
            <button
              className="card-ranker-add-btn"
              onClick={() => setShowAddPicker(p => !p)}
            >
              + Ajouter une carte
            </button>
            {showAddPicker && (
              <div className="card-ranker-picker">
                {addableCards.map(c => (
                  <div
                    key={c}
                    className="card-ranker-picker-item"
                    onClick={() => handleAddCard(c)}
                  >
                    <Card card={c} size="small" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {excluded.length > 0 && (
        <div className="card-ranker-section card-ranker-excluded">
          <h5 className="card-ranker-title">Non pertinentes</h5>
          {excluded.map(c => (
            <div key={c.card} className="card-rank-item excluded" onClick={() => handleRestore(c.card)}>
              <div className="card-rank-card">
                <Card card={c.card as CardNumber} size="small" disabled />
              </div>
              <span className="card-rank-restore" title="Remettre dans les pertinentes">↩</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default CardRanker;
